import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IntroContent as IntroContentType } from '../types';
import { useChatActions } from '../hooks/useChatActions';
import './IntroContent.css';

interface IntroContentProps {
  content: IntroContentType;
  onDismiss?: () => void;
}

export const IntroContent: React.FC<IntroContentProps> = ({ content, onDismiss }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const { dismissIntro } = useChatActions();
  const { t } = useTranslation();

  // Handle video loading
  useEffect(() => {
    if (content.type === 'video' && content.videoUrl && videoRef.current) {
      const video = videoRef.current;
      
      const handleLoadedData = () => {
        setIsVideoLoaded(true);
        setVideoError(false);
      };

      const handleError = () => {
        setVideoError(true);
        setIsVideoLoaded(false);
      };

      const handleEnded = () => {
        console.log('Video ended - preventing loop');
        // Prevent any restart attempts
        video.loop = false;
        video.currentTime = video.duration;
        // Remove autoplay attribute if it exists
        video.removeAttribute('autoplay');
        // Pause to ensure it's stopped
        video.pause();
      };

      video.addEventListener('loadeddata', handleLoadedData);
      video.addEventListener('error', handleError);
      video.addEventListener('ended', handleEnded);

      return () => {
        video.removeEventListener('loadeddata', handleLoadedData);
        video.removeEventListener('error', handleError);
        video.removeEventListener('ended', handleEnded);
      };
    }
  }, [content.type, content.videoUrl]);

  // Auto-play video when loaded (only once)
  const [hasAutoPlayed, setHasAutoPlayed] = useState(false);
  
  useEffect(() => {
    if (isVideoLoaded && videoRef.current && !hasAutoPlayed) {
      setHasAutoPlayed(true);
      videoRef.current.play().catch((error) => {
        console.warn('Auto-play failed:', error);
        // Auto-play might be blocked by browser policy, but that's okay
      });
    }
  }, [isVideoLoaded, hasAutoPlayed]);

  const handleDismiss = () => {
    dismissIntro();
    if (onDismiss) {
      onDismiss();
    }
  };

  const handleSkip = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    handleDismiss();
  };

  return (
    <div className="intro-content" role="dialog" aria-labelledby="intro-title" aria-modal="false">
      <div className="intro-content__container">
        <div className="intro-content__header">
          <h2 id="intro-title" className="intro-content__title">
            {t('intro.title')}
          </h2>
          <button
            className="intro-content__close-button"
            onClick={handleDismiss}
            aria-label={t('intro.closeButton')}
            type="button"
          >
            <svg
              className="intro-content__close-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="intro-content__body">
          {content.type === 'text' ? (
            <div className="intro-content__text">
              <p>{content.content}</p>
            </div>
          ) : (
            <div className="intro-content__video-container">
              {content.videoUrl && !videoError ? (
                <>
                  <video
                    ref={videoRef}
                    className="intro-content__video"
                    controls
                    preload="metadata"
                    loop={false}
                    autoPlay={false}
                    playsInline
                    onEnded={() => {
                      console.log('Video ended via onEnded prop');
                      if (videoRef.current) {
                        videoRef.current.loop = false;
                        videoRef.current.pause();
                      }
                    }}
                    aria-label="Introduction video"
                  >
                    <source src={content.videoUrl} type="video/mp4" />
                    <source src={content.videoUrl} type="video/webm" />
                    <source src={content.videoUrl} type="video/ogg" />
                    {t('intro.videoNotSupported')}
                  </video>
                  {!isVideoLoaded && (
                    <div className="intro-content__video-loading">
                      <div className="intro-content__spinner" aria-label={t('common.loading')}></div>
                      <p>{t('intro.videoLoading')}</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="intro-content__video-error">
                  <p>{t('intro.videoError')}</p>
                  <div className="intro-content__text">
                    <p>{content.content}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="intro-content__footer">
          <div className="intro-content__actions">
            {content.type === 'video' && content.videoUrl && !videoError && (
              <button
                className="intro-content__skip-button"
                onClick={handleSkip}
                type="button"
              >
                {t('intro.skipVideo')}
              </button>
            )}
            <button
              className="intro-content__dismiss-button"
              onClick={handleDismiss}
              type="button"
            >
              {t('intro.getStarted')}
            </button>
          </div>
          <p className="intro-content__hint">
            {t('intro.hint')}
          </p>
        </div>
      </div>
    </div>
  );
};